import React, { useEffect, useRef, useState } from 'react'

interface ResizableSectionProps {
  children: React.ReactNode
  storageKey: string
  defaultHeight: number
  minHeight?: number
  maxHeight?: number
  /** Set true on the last section to disable its handle (no sibling below). */
  last?: boolean
}

/**
 * Wraps a panel card so the user can drag a horizontal handle at the
 * bottom edge to resize its height. The chosen height is persisted to
 * localStorage under `storageKey`.
 */
export const ResizableSection: React.FC<ResizableSectionProps> = ({
  children,
  storageKey,
  defaultHeight,
  minHeight = 80,
  maxHeight = 800,
  last = false,
}) => {
  const [height, setHeight] = useState<number>(() => {
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        const n = parseInt(saved, 10)
        if (!isNaN(n)) return n
      }
    } catch {
      /* ignore */
    }
    return defaultHeight
  })
  const [resizing, setResizing] = useState(false)
  const startY = useRef(0)
  const startH = useRef(0)

  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    startY.current = e.clientY
    startH.current = height
    setResizing(true)
  }

  useEffect(() => {
    if (!resizing) return
    document.body.classList.add('resizing-panel-vertical')
    const onMove = (ev: MouseEvent) => {
      const dy = ev.clientY - startY.current
      const next = Math.max(minHeight, Math.min(maxHeight, startH.current + dy))
      setHeight(next)
    }
    const onUp = () => {
      setResizing(false)
      document.body.classList.remove('resizing-panel-vertical')
      try {
        localStorage.setItem(storageKey, String(height))
      } catch {
        /* ignore */
      }
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.classList.remove('resizing-panel-vertical')
    }
  }, [resizing, minHeight, maxHeight, storageKey, height])

  return (
    <div
      className="resizable-section"
      style={{
        // The "last" section grows to fill leftover vertical space so the
        // Tools palette is fully visible regardless of how the user sized
        // the previous sections. Non-last sections keep their persisted
        // height but may shrink (flex 0 1 auto) when the sum exceeds the
        // panel — without that, Tools would get pushed below the visible
        // area and the panel itself would need a scrollbar (which caused
        // the focus-scroll-bleed bug fixed in b73eeb0e).
        flex: last ? '1 1 auto' : `0 1 ${height}px`,
        minHeight,
        maxHeight: last ? undefined : maxHeight,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>{children}</div>
      {!last && (
        <div
          onMouseDown={onMouseDown}
          title="Drag to resize section"
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 8,
            cursor: 'ns-resize',
            background: resizing ? 'rgba(0,122,204,0.3)' : '#e8ecf1',
            zIndex: 5,
            borderTop: '1px solid #d0d7de',
          }}
        />
      )}
    </div>
  )
}
